import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import "../../../../styles/global.css";
import { commonService } from "@/services/commonService";
import { extractAbbreviation, useTimezone } from "@/contexts/TimeZoneContext";
// import { Tooltip } from "primereact/tooltip";

type TimeZone = {
  tzId: number;
  timeZoneDesc: string;
  timeZoneLabel: string;
  isActive: boolean;
  ianaTimeZone: string;
};

const parseUtcOffset = (label: string): number => {
  // Special case: exact "UTC"
  if (label === "UTC") return 0;

  const match = label.match(/UTC([+-])(\d{2}):(\d{2})/);
  if (!match) return 0; // fallback if no match

  const sign = match[1] === "-" ? -1 : 1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);

  return sign * (hours * 60 + minutes);
};

const sortTimeZones = (timeZones: TimeZone[]): TimeZone[] => {
  return [...timeZones].sort((a, b) => {
    const offsetA = parseUtcOffset(a.timeZoneLabel);
    const offsetB = parseUtcOffset(b.timeZoneLabel);
    return offsetA - offsetB;
  });
};

const TimezoneDropdown: React.FC = () => {
  const { timezone, setTimezone } = useTimezone();
  const [timeZones, setTimeZones] = useState<TimeZone[]>([]);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    const GetAllTimeZones = async () => {
      try {
        const res = await commonService.GetTimeZones();
        setTimeZones(res);
      } catch (error) {
        console.error("Error fetching timezones:", error);
        setIsReady(true);
      }
    };
    GetAllTimeZones();
  }, []);

  // Validation and Default Setting Logic
  useEffect(() => {
    if (timeZones.length === 0) return;
    // 1. Check if current context timezone is valid (exists in the API list)
    const isValid =
      timezone &&
      timeZones.some((tz) => tz.timeZoneLabel === timezone.timeZoneLabel);

    if (!isValid) {
      // 2. Get User's local offset in minutes
      // getTimezoneOffset() returns positive for West (UTC-5 is 300)
      // so we multiply by -1 to match our parseUtcOffset logic.
      const userOffsetMinutes = -new Date().getTimezoneOffset();

      // 3. Find the closest matching timezone from the list
      const matchedTz = timeZones.find(
        (tz) => parseUtcOffset(tz.timeZoneLabel) === userOffsetMinutes,
      );

      // 4. Fallback: If no offset match, use UTC or the first one, else set matched
      if (matchedTz) {
        setTimezone(matchedTz);
      } else {
        const utcFallback =
          timeZones.find((tz) => tz.timeZoneLabel === "UTC") || timeZones[0];
        setTimezone(utcFallback);
      }
    }
    setIsReady(true);
  }, [timeZones, timezone, setTimezone]);

  const sortedTimeZones = sortTimeZones(timeZones);
  const filteredTimeZones = sortedTimeZones.filter(
    (tz) =>
      tz.timeZoneLabel === "UTC" || parseUtcOffset(tz.timeZoneLabel) !== 0,
  );
  const valueTemplate = () => {
    // if (!isReady || !timezone) return <span>Select Time Zone</span>;
    if (!isReady || !timezone) {
      return (
        <i
          className="pi pi-spin pi-spinner text-[var(--text-primary)]"
          style={{ fontSize: "1rem" }}
        ></i>
      );
    }

    return (
      <span className="font-semibold text-[var(--text-primary)]">
        {extractAbbreviation(timezone.timeZoneLabel)}
      </span>
    );
  };

  return (
    <div className="timezone-chip flex flex-row items-center gap-1.5 px-2.5 h-9 rounded-lg bg-transparent cursor-pointer hover:bg-transparent transition duration-150 text-[var(--text-primary)]">
      <div className="tooltip-wrapper">
        <i className="fa-solid fa-earth-asia text-sm text-[var(--text-primary)] align-middle"></i>
        <span className="tooltip-text">Timezone</span>
      </div>

      <Dropdown
        value={timezone}
        onChange={(e) => setTimezone(e.value)}
        options={filteredTimeZones}
        optionLabel="timeZoneLabel"
        placeholder="Select Time Zone"
        className="tmz"
        panelClassName="tmz-panel"
        valueTemplate={valueTemplate}
        disabled={!isReady}
      />
    </div>
  );
};

export default TimezoneDropdown;
